import { inject, Injectable } from '@angular/core';
import { StadiaMapsService } from '../stadia-maps/stadia-maps.service';
import { getStadiaMapsAddressDetails } from '../stadia-maps/stadia-maps-address-details';
import { AddressDetails, Country } from '@pedix-workspace/utils';
import { getGoogleMapsAddressDetails } from '../google-maps/google-maps-address-details';
import {
  GoogleMapsService,
  geocoderResultToLookupResult,
} from '../google-maps/google-maps.service';

export type MapAddressAutocompleteRequest = {
  q: string;
  at: string;
  limit: number;
  countryCodes?: Country[];
};
export type MapAddressLookupRequest = {
  source: 'google-maps' | 'here-maps' | 'stadia-maps' | 'empty';
  selectedPrediction: MapAddressAutocompleteItem;
  updateTimezone: boolean;
  searchTerm?: string | null;
};
export type MapAddressReverseGeocodingRequest = {
  latitude: number;
  longitude: number;
  updateTimezone: boolean;
  searchTerm?: string | null;
};
export type MapAddressAutocompleteResponse = {
  source: 'google-maps' | 'here-maps' | 'stadia-maps' | 'empty';
  searchTerm: string;
  items: MapAddressAutocompleteItem[];
};
export type MapAddressAutocompleteItem = {
  id: string;
  title: string;
  highlights: {
    start: number;
    end: number;
  }[];
};

@Injectable({ providedIn: 'root' })
export class MapAddressService {
  private googleMapsService = inject(GoogleMapsService);
  private stadiaMapsService = inject(StadiaMapsService);

  autocomplete(request: MapAddressAutocompleteRequest): Promise<MapAddressAutocompleteResponse> {
    const regionCodes = request.countryCodes
      ? request.countryCodes.filter(countryCode => countryCode !== 'XX')
      : undefined;

    return this.googleMapsService
      .autocomplete({
        q: request.q,
        location: request.at,
        radius: 15000,
        regionCodes,
      })
      .then(async gmResponse => {
        return {
          source: 'google-maps',
          searchTerm: request.q,
          items: (gmResponse.suggestions || []).map(suggestion => ({
            id: suggestion.placePrediction.placeId,
            title: suggestion.placePrediction.text.text,
            highlights: suggestion.placePrediction.text.matches?.map(match => ({
              start: match.startOffset,
              end: match.endOffset,
            })),
          })),
        };
      });
  }

  placeDetails(request: MapAddressLookupRequest): Promise<AddressDetails> {
    if (request.source === 'google-maps') {
      return this.googleMapsService
        .lookup({ id: request.selectedPrediction.id })
        .then(async gmResponse => {
          const addressDetails = getGoogleMapsAddressDetails({
            place: gmResponse,
            selectedPrediction: request.selectedPrediction,
            freeSearch: request.searchTerm,
          });

          if (request.updateTimezone) {
            addressDetails.timezone = await this.googleMapsService.getTimezone(gmResponse.location);
          }

          return addressDetails;
        });
    }
    return Promise.reject();
  }

  reverseGeocoding(request: MapAddressReverseGeocodingRequest): Promise<AddressDetails> {
    return this.stadiaMapsService
      .reverseGeocode({
        latitude: request.latitude,
        longitude: request.longitude,
        types: ['address'],
      })
      .then(async response => {
        const filteredFeatures = (response.features || []).filter(
          feature => feature.properties.confidence >= 0.9,
        );

        if (filteredFeatures.length > 0) {
          const addressDetails = getStadiaMapsAddressDetails(
            response.features[0],
            request.searchTerm,
            {
              lat: request.latitude,
              lng: request.longitude,
            },
          );
          if (request.updateTimezone) {
            addressDetails.timezone = await this.stadiaMapsService.getTimezone({
              latitude: request.latitude,
              longitude: request.longitude,
            });
          }
          return addressDetails;
        }
        return this.googleMapsService.reverseGeocode(request).then(async gmResponse => {
          const firstMatch =
            gmResponse.results.find(result => result.types.includes('street_address')) ||
            gmResponse.results[0];
          const lookupResult = geocoderResultToLookupResult(firstMatch);
          const addressDetails = getGoogleMapsAddressDetails({
            place: lookupResult,
            freeSearch: request.searchTerm,
            geolocation: { lat: request.latitude, lng: request.longitude },
          });

          if (request.updateTimezone) {
            addressDetails.timezone = await this.googleMapsService.getTimezone(
              lookupResult.location,
            );
          }
          return addressDetails;
        });
      });
  }
}
